import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    OverlayDrawer,
    Spinner,
    Switch,
} from '@fluentui/react-components';
import {
    ArrowClockwise20Regular,
    Dismiss24Regular,
    Info20Regular,
    Sparkle20Regular,
} from '@fluentui/react-icons';
import moment from 'moment';

import * as ComponentFunctions from './ConsumptionSyncProcessIAAnalysis.Functions';
import SyncAnalysisFindingCard from './SyncAnalysisFinding.Card';
import SyncAnalysisSummaryComponent from './SyncAnalysisSummary.Component';
import SettlementRegressionsComponent from '../../SettlementRegressions/SettlementRegressions.Component';
import { IAAgentAvatar } from '../../../../commons/component/IAAgentAvatar/IAAgentAvatar.Component';
import { SyncAnalysisFinding, ISyncAnalysisFindingsFilters } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { SyncAnalysisFindingDecisionEnum } from '../../../../models/Enums/SyncAnalysisFindingDecisionEnum';
import { SyncAnalysisStatusEnum } from '../../../../models/Enums/SyncAnalysisStatusEnum';
import { PaginationControlComponent } from '../../../../commons/component/PaginationControl/PaginationControl.Component';
import { SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE } from '../../../../redux/middleware/ProtocolConsumptionSyncAnalysisMiddleware';

import './ConsumptionSyncProcessIAAnalysis.Styles.css';

type PanelTab = 'run' | 'regressions';

/**
 * La explicación de regresiones está implementada pero oculta hasta terminar de
 * definirla con el backend. Con true vuelve a aparecer la solapa.
 */
const SHOW_REGRESSIONS = false;

/**
 * El panel de Alphi sobre una corrida de sincronización. Dos lecturas, a pedido
 * del analista: el análisis de la corrida (qué quedó sin liquidar y por qué) y la
 * explicación de regresiones del protocolo (qué dejó de liquidarse respecto de antes).
 */
const ConsumptionSyncProcessIAAnalysisComponent = (props: ComponentFunctions.Props) => {
    const { analysis } = props;
    const [tab, setTab] = useState<PanelTab>('run');

    // El servidor pagina y filtra; filtrar acá sólo alcanzaría a la página
    // cargada y dejaría mal los totales y el recorrido de páginas.
    const [selectedType, setSelectedType] = useState<SyncAnalysisFindingTypeEnum | undefined>(undefined);
    const [selectedDecision, setSelectedDecision] = useState<SyncAnalysisFindingDecisionEnum | undefined>(undefined);
    const [onlyNew, setOnlyNew] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE);

    const filters: ISyncAnalysisFindingsFilters = useMemo(
        () => ({ findingType: selectedType, decision: selectedDecision, onlyNew: onlyNew || undefined }),
        [selectedType, selectedDecision, onlyNew]
    );

    // Cambiar un filtro vuelve a la primera página: quedarse en la 7 de un
    // listado que ahora tiene 2 mostraría una página vacía.
    useEffect(() => {
        setPageNumber(1);
    }, [selectedType, selectedDecision, onlyNew, pageSize]);

    useEffect(() => {
        if (props.isOpen && props.SyncProcessId) {
            props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.SyncProcessId]);

    // Los hallazgos se piden recién cuando hay un análisis terminado que los tenga.
    const analysisId = analysis?.id;
    const canListFindings = ComponentFunctions.hasFindingsToShow(analysis);

    useEffect(() => {
        if (props.isOpen && analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, analysisId, canListFindings, pageNumber, pageSize, filters]);

    const onRefresh = () => {
        props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        if (analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    };

    // Un análisis nuevo reemplaza al anterior: los filtros y la página que estaban
    // puestos son de hallazgos que ya no existen.
    const onExecute = () => {
        setSelectedType(undefined);
        setSelectedDecision(undefined);
        setOnlyNew(false);
        setPageNumber(1);
        props.ExecuteProtocolConsumptionSyncAnalysis(props.SyncProcessId);
    };

    // La decisión del analista sobre un hallazgo: la tarjeta la pide, el store la
    // guarda y la corrida relee sus contadores.
    const onDecide = (finding: SyncAnalysisFinding, decision: SyncAnalysisFindingDecisionEnum, reason?: string) => {
        props.SetProtocolConsumptionSyncAnalysisFindingDecision(props.SyncProcessId, {
            findingId: finding.id,
            decision,
            reason: reason ?? null,
        });
    };

    const statusVariant = ComponentFunctions.getStatusVariant(analysis?.status);
    const typeCounts = useMemo(() => ComponentFunctions.buildFindingTypeCounts(analysis), [analysis]);
    const totalFindings = useMemo(() => ComponentFunctions.getTotalFindingCount(analysis), [analysis]);

    const executedAt = analysis?.executedAt
        ? moment(analysis.executedAt).format('DD/MM/YYYY HH:mm')
        : undefined;

    const isFirstLoad = props.isGettingAnalysis && analysis === undefined;
    const notAnalysed = !props.isGettingAnalysis && analysis === null;

    // El análisis corre del lado del servidor: mientras el POST espera respuesta, y
    // también si lo disparó otro, el análisis queda persistido en curso.
    const analysisIsRunning = ComponentFunctions.isAnalysisRunning(analysis);
    const isRunning = props.isExecutingAnalysis || analysisIsRunning;

    // ── Sondeo ──
    // El servidor no avisa nada: el panel relee mientras el análisis corre.
    const pollCount = useRef<number>(0);
    const [pollingGaveUp, setPollingGaveUp] = useState<boolean>(false);

    // El tope se cuenta por análisis, no por vida del panel.
    useEffect(() => {
        if (!isRunning) {
            pollCount.current = 0;
            setPollingGaveUp(false);
        }
    }, [isRunning]);

    useEffect(() => {
        if (!props.isOpen || !isRunning || pollingGaveUp) return;

        const timer = window.setInterval(() => {
            if (pollCount.current >= ComponentFunctions.SYNC_ANALYSIS_MAX_POLLS) {
                // Un análisis que quedó marcado en curso porque el proceso murió no
                // se despierta nunca: se corta y se le avisa al usuario.
                setPollingGaveUp(true);
                return;
            }

            pollCount.current += 1;
            props.RefreshProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        }, ComponentFunctions.SYNC_ANALYSIS_POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.SyncProcessId, isRunning, pollingGaveUp]);

    // El análisis es a pedido: se ofrece sobre una corrida terminada, tanto la
    // primera vez como para volver a analizar. Si el sondeo se dio por vencido,
    // el "en curso" que bloqueaba es casi seguro un proceso muerto: se deja pedir.
    const canExecute = ComponentFunctions.canRequestAnalysis({
        syncProcessFinished: props.SyncProcessFinished,
        analysis,
        isExecuting: props.isExecutingAnalysis,
    }) || (pollingGaveUp && props.SyncProcessFinished && !props.isExecutingAnalysis);

    const executeLabel = analysis ? 'Volver a analizar' : 'Analizar ahora';
    let executeBlockedReason: string | undefined;
    if (!props.SyncProcessFinished) executeBlockedReason = 'La corrida todavía no terminó.';
    else if (props.isExecutingAnalysis) executeBlockedReason = 'Ya hay un pedido de análisis en viaje.';
    else if (analysisIsRunning && !pollingGaveUp) executeBlockedReason = 'Hay un análisis en curso; esperá a que termine.';

    return (
        <OverlayDrawer
            position="end"
            size="medium"
            open={props.isOpen}
            onOpenChange={(_, data) => {
                if (!data.open) props.onDismiss();
            }}
        >
            <DrawerHeader className="csa-header">
                <DrawerHeaderTitle
                    action={
                        <div className="csa-header__actions">
                            {tab === 'run' && (
                                <>
                                    {/* El accionable principal, siempre a la vista: deshabilitado
                                        dice por qué en lugar de desaparecer. */}
                                    <Button
                                        appearance="primary"
                                        size="small"
                                        icon={props.isExecutingAnalysis ? <Spinner size="extra-tiny" /> : <Sparkle20Regular />}
                                        disabled={!canExecute}
                                        title={executeBlockedReason ?? executeLabel}
                                        onClick={onExecute}
                                    >
                                        {executeLabel}
                                    </Button>
                                    <Button
                                        appearance="subtle"
                                        icon={<ArrowClockwise20Regular />}
                                        aria-label="Actualizar"
                                        title="Actualizar"
                                        // Sigue disponible durante la corrida: es un GET, y es la
                                        // salida si el sondeo se dio por vencido.
                                        disabled={props.isGettingAnalysis}
                                        onClick={onRefresh}
                                    />
                                </>
                            )}
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                aria-label="Cerrar"
                                onClick={props.onDismiss}
                            />
                        </div>
                    }
                >
                    <span className="csa-header__title">
                        <IAAgentAvatar size={78} className="csa-header__titleAvatar" />
                        <span className="csa-header__titleText">
                            Alphi · Análisis de liquidación
                            <Badge appearance="filled" color="warning" size="small">PREVIEW</Badge>
                        </span>
                    </span>
                </DrawerHeaderTitle>
                <span className="csa-header__subtitle">
                    {SHOW_REGRESSIONS
                        ? 'Se pide a mano, cuando el analista lo necesita. Dos lecturas: la corrida, y qué dejó de liquidarse respecto de antes.'
                        : 'Se pide a mano, cuando el analista lo necesita: qué quedó sin liquidar en esta corrida y por qué.'}
                </span>
                {SHOW_REGRESSIONS && (
                    <div className="csa-tabs" role="tablist" aria-label="Qué mirar">
                        <button type="button" role="tab" aria-selected={tab === 'run'} className={'csa-tab' + (tab === 'run' ? ' is-active' : '')} onClick={() => setTab('run')}>
                            Esta corrida
                        </button>
                        <button type="button" role="tab" aria-selected={tab === 'regressions'} className={'csa-tab' + (tab === 'regressions' ? ' is-active' : '')} onClick={() => setTab('regressions')}>
                            Regresiones de liquidación
                        </button>
                    </div>
                )}
            </DrawerHeader>

            <DrawerBody className="csa-body">

                {SHOW_REGRESSIONS && tab === 'regressions' && (
                    <SettlementRegressionsComponent
                        ProtocolId={props.ProtocolId}
                        CurrentSyncProcessId={props.SyncProcessId}
                        isActive={props.isOpen && tab === 'regressions'}
                    />
                )}

                {tab === 'run' && (
                    <>
                        {isFirstLoad && (
                            <div className="csa-loading">
                                <Spinner size="tiny" />
                                <span>Buscando el análisis de esta corrida...</span>
                            </div>
                        )}

                        {/* El análisis corre del lado del servidor y puede demorar minutos: sin
                            este aviso, el panel parecería colgado. */}
                        {isRunning && (
                            <div className="csa-running">
                                {!pollingGaveUp && <Spinner size="tiny" />}
                                <div className="csa-running__text">
                                    <span className="csa-running__title">
                                        {pollingGaveUp
                                            ? 'El análisis figura en curso'
                                            : 'Analizando la corrida...'}
                                    </span>
                                    <span className="csa-running__note">
                                        {pollingGaveUp
                                            ? 'Sigue así después de un buen rato, así que el proceso pudo haber quedado interrumpido. El panel dejó de actualizarse solo: probá con Actualizar, o volvé a pedir el análisis.'
                                            : 'Revisa las visitas y la parametrización paciente por paciente. El panel se actualiza solo hasta que termine.'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Que la corrida no tenga análisis es lo normal hasta que alguien lo
                            pide: éste es el lugar para pedirlo. */}
                        {notAnalysed && !props.failOnGettingAnalysis && !isRunning && (
                            <MessageBar intent="info">
                                <MessageBarBody>
                                    <MessageBarTitle>Sin análisis</MessageBarTitle>
                                    {props.SyncProcessFinished
                                        ? 'Esta corrida todavía no fue analizada. Pedile a Alphi que la revise: mira qué quedó sin liquidar y por qué.'
                                        : 'Esta corrida todavía está en curso. El análisis se puede pedir cuando la sincronización termine.'}
                                </MessageBarBody>
                                {canExecute && (
                                    <MessageBarActions>
                                        <Button
                                            appearance="primary"
                                            size="small"
                                            icon={<Sparkle20Regular />}
                                            onClick={onExecute}
                                        >
                                            Analizar ahora
                                        </Button>
                                    </MessageBarActions>
                                )}
                            </MessageBar>
                        )}

                        {/* La ejecución falla por motivos que el análisis persistido no cuenta
                            —no está habilitada, ya hay una en curso—, así que se muestran aparte. */}
                        {props.failOnExecutingAnalysis && (
                            <MessageBar intent="error">
                                <MessageBarBody>
                                    <MessageBarTitle>No se pudo ejecutar el análisis</MessageBarTitle>
                                    {props.executionError?.ErrorMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}

                        {props.failOnGettingAnalysis && (
                            <MessageBar intent="error">
                                <MessageBarBody>
                                    <MessageBarTitle>No se pudo obtener el análisis</MessageBarTitle>
                                    {props.error?.ErrorMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}

                        {analysis && (
                            <>
                                {/* ── Estado ── */}
                                <section className={'csa-status csa-status--' + statusVariant}>
                                    <div className="csa-status__row">
                                        <span className={'csa-status__pill csa-status__pill--' + statusVariant}>
                                            {ComponentFunctions.getStatusLabel(analysis.status)}
                                        </span>
                                        {executedAt && <span className="csa-status__date">{executedAt}</span>}
                                        {analysis.requestedBy && (
                                            <span className="csa-status__by">por {analysis.requestedBy}</span>
                                        )}
                                    </div>

                                    {/* No haberse ejecutado por falta de indicios es una decisión
                                        deliberada del proceso: se explica para que no se lea
                                        como una falla ni como un análisis vacío. */}
                                    {analysis.status === SyncAnalysisStatusEnum.SkippedNoIndicators && (
                                        <p className="csa-status__note">
                                            <Info20Regular />
                                            No se ejecutó porque la verificación previa no encontró indicios de
                                            liquidación faltante. La corrida quedó registrada igual.
                                        </p>
                                    )}

                                    {analysis.status === SyncAnalysisStatusEnum.Failed && analysis.errorMessage && (
                                        <p className="csa-status__error">{analysis.errorMessage}</p>
                                    )}
                                </section>

                                {/* ── Resumen ── */}
                                {analysis.summary && (
                                    <section className="csa-summary">
                                        <span className="csa-summary__title">Resumen</span>
                                        <SyncAnalysisSummaryComponent summary={analysis.summary} />
                                    </section>
                                )}

                                {/* ── Métricas ──
                                    Mientras el análisis corre, los contadores todavía están en
                                    cero: se ocultan porque una fila de ceros se lee como un
                                    resultado —"no encontró nada"— y no lo es. */}
                                {!analysisIsRunning && (
                                    <section className="csa-metrics">
                                        <div className="csa-metric">
                                            <span className="csa-metric__label">Evidencia analizada</span>
                                            <span className="csa-metric__value">{analysis.evidenceItemCount}</span>
                                        </div>
                                        <div className="csa-metric">
                                            <span className="csa-metric__label">Hallazgos</span>
                                            <span className="csa-metric__value">{totalFindings}</span>
                                        </div>
                                        <div className="csa-metric">
                                            <span className="csa-metric__label">Pendientes</span>
                                            <span className="csa-metric__value">{analysis.pendingFindingCount}</span>
                                        </div>
                                        {analysis.discardedFindingCount > 0 && (
                                            <div className="csa-metric">
                                                <span className="csa-metric__label">Descartados</span>
                                                <span className="csa-metric__value">{analysis.discardedFindingCount}</span>
                                            </div>
                                        )}
                                    </section>
                                )}

                                {/* ── Hallazgos ── */}
                                {canListFindings && (
                                    <section className="csa-findings">
                                        <header className="csa-findings__head">
                                            <span className="csa-findings__title">Hallazgos</span>
                                            <Switch
                                                checked={onlyNew}
                                                onChange={(_, data) => setOnlyNew(!!data.checked)}
                                                label="Sólo nuevos"
                                            />
                                        </header>

                                        {/* Los contadores por tipo son a la vez el desglose y el
                                            filtro: son las mismas cifras que se querrían tocar. */}
                                        {typeCounts.length > 0 && (
                                            <div className="csa-typeFilters" role="group" aria-label="Filtrar por tipo">
                                                <button
                                                    type="button"
                                                    className={
                                                        'csa-typeChip' + (selectedType === undefined ? ' is-active' : '')
                                                    }
                                                    onClick={() => setSelectedType(undefined)}
                                                >
                                                    Todos
                                                    <span className="csa-typeChip__count">{totalFindings}</span>
                                                </button>
                                                {typeCounts.map(({ descriptor, count }) => (
                                                    <button
                                                        key={descriptor.type}
                                                        type="button"
                                                        title={descriptor.description + ' Ancla en: ' + descriptor.anchor + '.'}
                                                        className={
                                                            'csa-typeChip csa-typeChip--type' + descriptor.type +
                                                            (selectedType === descriptor.type ? ' is-active' : '')
                                                        }
                                                        onClick={() =>
                                                            setSelectedType(
                                                                selectedType === descriptor.type ? undefined : descriptor.type
                                                            )
                                                        }
                                                    >
                                                        {descriptor.label}
                                                        <span className="csa-typeChip__count">{count}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Por decisión: lo que falta revisar, lo confirmado y lo descartado.
                                            Es el recorrido del analista, así que va como filtro aparte del tipo. */}
                                        <div className="csa-decisionFilters" role="group" aria-label="Filtrar por decisión">
                                            <button
                                                type="button"
                                                className={'csa-typeChip' + (selectedDecision === undefined ? ' is-active' : '')}
                                                onClick={() => setSelectedDecision(undefined)}
                                            >
                                                Todas las decisiones
                                            </button>
                                            {ComponentFunctions.DECISIONS.map(item => (
                                                <button
                                                    key={item.decision}
                                                    type="button"
                                                    title={item.description}
                                                    className={'csa-typeChip csa-typeChip--' + item.variant + (selectedDecision === item.decision ? ' is-active' : '')}
                                                    onClick={() => setSelectedDecision(selectedDecision === item.decision ? undefined : item.decision)}
                                                >
                                                    {item.label}
                                                    {item.decision === SyncAnalysisFindingDecisionEnum.Pending && analysis.pendingFindingCount > 0 && (
                                                        <span className="csa-typeChip__count">{analysis.pendingFindingCount}</span>
                                                    )}
                                                    {item.decision === SyncAnalysisFindingDecisionEnum.Discarded && analysis.discardedFindingCount > 0 && (
                                                        <span className="csa-typeChip__count">{analysis.discardedFindingCount}</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>

                                        {props.failOnGettingFindings && (
                                            <MessageBar intent="error">
                                                <MessageBarBody>
                                                    <MessageBarTitle>No se pudieron obtener los hallazgos</MessageBarTitle>
                                                    {props.error?.ErrorMessage}
                                                </MessageBarBody>
                                            </MessageBar>
                                        )}

                                        {props.isGettingFindings && props.findings.length === 0 && (
                                            <div className="csa-loading">
                                                <Spinner size="extra-tiny" />
                                                <span>Cargando hallazgos...</span>
                                            </div>
                                        )}

                                        {!props.isGettingFindings && props.findings.length === 0 && (
                                            <p className="csa-findings__empty">
                                                {selectedType !== undefined || selectedDecision !== undefined || onlyNew
                                                    ? 'Ningún hallazgo coincide con el filtro.'
                                                    : 'El análisis no dejó hallazgos.'}
                                            </p>
                                        )}

                                        {props.findings.length > 0 && (
                                            <ul className="csa-findings__list">
                                                {props.findings.map((finding: SyncAnalysisFinding) => (
                                                    <SyncAnalysisFindingCard
                                                        key={finding.id}
                                                        finding={finding}
                                                        onDecide={onDecide}
                                                        isDeciding={props.isSettingDecision && props.decisionFindingId === finding.id}
                                                        decisionError={props.failOnSettingDecision && props.decisionFindingId === finding.id
                                                            ? (props.decisionError?.ErrorMessage || 'No se pudo guardar la decisión.')
                                                            : undefined}
                                                    />
                                                ))}
                                            </ul>
                                        )}

                                        <PaginationControlComponent
                                            paginationState={props.findingsPagination}
                                            showedRecords={props.findings.length}
                                            isLoading={props.isGettingFindings}
                                            setPageNumber={setPageNumber}
                                            setPageSize={setPageSize}
                                        />
                                    </section>
                                )}
                            </>
                        )}
                    </>
                )}
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionSyncProcessIAAnalysisComponent as any) as React.FC<ComponentFunctions.IProps>;
